// src/components/forms/variants/split/SplitEmailCapture.tsx

'use client'

import { forwardRef, useMemo, useCallback } from 'react'
import Image from 'next/image'
import { motion } from 'motion/react'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import type { RenderProps } from '~/components/forms/base/types'
import type { SplitEmailCaptureProps } from '~/components/forms/variants/split/types'
import { SPLIT_PRESETS } from '~/components/forms/variants/split/types'
import { siteConfig } from '~/lib/config/site'

/**
 * Split Email Capture Form Component
 * 
 * A form variant that displays content split into two columns - one for the form and one for an image.
 * Supports two layout presets: modern and classic.
 */
export const SplitEmailCapture = forwardRef<HTMLFormElement, SplitEmailCaptureProps>(({
  preset = 'modern',
  image,
  theme: customTheme,
  content: customContent,
  ...props
}, ref) => {
  // Memoize merged configurations
  const theme = useMemo(() => ({
    ...SPLIT_PRESETS[preset]?.theme,
    ...customTheme,
    className: {
      ...SPLIT_PRESETS[preset]?.theme?.className,
      ...customTheme?.className
    }
  }), [preset, customTheme])

  const content = useMemo(() => ({
    ...SPLIT_PRESETS[preset]?.content,
    ...customContent,
    button: {
      ...SPLIT_PRESETS[preset]?.content?.button,
      ...customContent?.button
    },
    input: {
      ...SPLIT_PRESETS[preset]?.content?.input,
      ...customContent?.input
    }
  }), [preset, customContent])

  // Memoize the form render function
  const renderForm = useCallback(({ state, handlers }: RenderProps) => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    return (
      <div className={cn(
        'grid grid-cols-1 md:grid-cols-2 gap-0 items-stretch overflow-hidden',
        'bg-white dark:bg-gray-900',
        theme.className?.container
      )}>
        {/* Image Side */}
        <div className={cn(
          'relative overflow-hidden h-full',
          theme.imagePosition === 'left' ? 'md:order-first' : 'md:order-last'
        )}>
          <div className="absolute inset-0">
            <motion.div 
              className="h-full w-full"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4 }}
            >
              <div className="relative h-full w-full min-h-[600px]">
                <Image
                  src={image.src}
                  alt={image.alt ?? ''}
                  fill
                  className={cn(
                    "object-cover",
                    image.className
                  )}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                {/* Overlay */}
                {image.overlay?.enabled !== false && (
                  <div 
                    className={cn(
                      "absolute inset-0",
                      image.overlay?.blur && "backdrop-blur-sm",
                      "mix-blend-multiply"
                    )}
                    style={{ 
                      background: image.overlay?.gradient ?? 
                        image.overlay?.color ?? 
                        'linear-gradient(to bottom right, rgba(79, 70, 229, 0.8), rgba(124, 58, 237, 0.8))',
                      opacity: image.overlay?.opacity ?? 1
                    }}
                  />
                )}
              </div>
            </motion.div>
          </div>

          {/* Floating Content */}
          {(image.floatingContent?.badge ?? image.floatingContent?.stats ?? image.floatingContent?.bottomContent) && (
            <div className="relative flex h-full min-h-[600px] flex-col justify-between p-8 text-white">
              {/* Badge */}
              {image.floatingContent.badge && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "flex items-center gap-3",
                    image.floatingContent.badge.className
                  )}
                >
                  {image.floatingContent.badge.icon && (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
                      {image.floatingContent.badge.icon}
                    </div>
                  )}
                  <div className="text-sm font-medium">
                    <div className="text-white/80">{image.floatingContent.badge.text}</div>
                    {image.floatingContent.badge.subText && (
                      <div className="text-white">{image.floatingContent.badge.subText}</div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Stats */}
              {image.floatingContent.stats && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-4"
                >
                  {image.floatingContent.stats.map((stat, index) => (
                    <div 
                      key={index}
                      className={cn(
                        "rounded-2xl bg-white/10 p-6 backdrop-blur-sm",
                        stat.className
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white/80">{stat.label}</div>
                          <div className="text-2xl font-semibold">{stat.value}</div>
                        </div>
                        {stat.icon && (
                          <motion.div 
                            className="h-12 w-12 rounded-full bg-white/20 p-3"
                            animate={{ 
                              rotate: [0, 10, 0],
                              scale: [1, 1.1, 1]
                            }}
                            transition={{ 
                              duration: 2,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                          >
                            {stat.icon}
                          </motion.div>
                        )}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Bottom Content */}
              {image.floatingContent.bottomContent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-auto"
                >
                  {image.floatingContent.bottomContent}
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Form Content */}
        <div className={cn(
          "flex flex-col space-y-8 p-10",
          "bg-white dark:bg-gray-900",
          "border-l border-gray-100 dark:border-gray-800",
          theme.className?.form
        )}>
          {/* Badge (if using classic preset) */}
          {preset === 'classic' && content.badge && (
            <div className="flex items-center gap-2 -mb-2">
              <span className={cn(
                "inline-flex items-center rounded-full px-3 py-1",
                "bg-indigo-50 dark:bg-indigo-900/20",
                "text-sm font-medium text-indigo-700 dark:text-indigo-300",
                "ring-1 ring-inset ring-indigo-700/10 dark:ring-indigo-400/20",
                content.badge.className
              )}>
                {content.badge.text}
              </span>
            </div>
          )}

          {/* Header */}
          <div className="space-y-3">
            {content.title && (
              <h2 className={cn(
                'text-3xl font-bold tracking-tight',
                'text-gray-900 dark:text-white',
                content.title.className
              )}>
                {content.title.text}
              </h2>
            )}
            {content.subtitle && (
              <p className={cn(
                'text-lg text-gray-600 dark:text-gray-400',
                content.subtitle.className
              )}>
                {content.subtitle.text}
              </p>
            )}
          </div>

          {/* Features (if classic preset, show above form) */}
          {preset === 'classic' && content.features && content.features.length > 0 && (
            <div className="grid grid-cols-1 gap-4 mb-8">
              {content.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  {feature.icon ? (
                    <div className="rounded-full bg-indigo-50 dark:bg-indigo-900/20 p-1">
                      <div className="h-4 w-4 text-indigo-600 dark:text-indigo-400">
                        {feature.icon}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-full bg-indigo-50 dark:bg-indigo-900/20 p-1">
                      <RiCheckLine className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                  )}
                  <span className="text-sm text-gray-600 dark:text-gray-400">{feature.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {preset === 'classic' ? (
              <div className="relative">
                <div className="relative flex flex-col space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={content.input?.placeholder}
                    className={cn(
                      'block w-full rounded-xl border-0 px-4 py-4',
                      'text-gray-900 dark:text-white',
                      'bg-gray-50 dark:bg-gray-800/50',
                      'shadow-sm ring-1 ring-inset',
                      'ring-gray-200 dark:ring-gray-700/50',
                      'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                      'focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400',
                      'hover:ring-gray-300 dark:hover:ring-gray-600',
                      'transition-shadow duration-200',
                      'sm:text-sm sm:leading-6',
                      error && 'ring-red-300 dark:ring-red-500/30 focus:ring-red-500',
                      content.input?.className
                    )}
                    disabled={isSubmitting || isSuccess}
                  />
                  <button
                    type="submit"
                    onClick={async (e) => {
                      e.preventDefault()
                      await handleSubmit(e)
                    }}
                    disabled={isSubmitting || isSuccess}
                    aria-label={isSubmitting ? content.button?.loadingText : content.button?.text}
                    className={cn(
                      'flex w-full items-center justify-center gap-2 rounded-xl',
                      'bg-indigo-600 dark:bg-indigo-500',
                      'px-4 py-4 text-base font-semibold text-white shadow-sm',
                      'hover:bg-indigo-500 dark:hover:bg-indigo-400',
                      'focus:outline-none focus:ring-2',
                      'focus:ring-indigo-500 dark:focus:ring-indigo-400',
                      'focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                      'disabled:opacity-50 dark:disabled:opacity-40',
                      'transition-all duration-200',
                      'transform hover:scale-[1.02] active:scale-[0.98]',
                      content.button?.className
                    )}
                  >
                    <span>{isSubmitting ? content.button?.loadingText : content.button?.text}</span>
                    {content.button?.icon}
                  </button>
                </div>
                {error && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
                    {error}
                  </p>
                )}
                {isSuccess && (
                  <p className="mt-2 text-sm text-green-600 dark:text-green-400" role="alert">
                    {content.messages?.success}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={content.input?.placeholder}
                    className={cn(
                      'block w-full rounded-lg border-0 px-4 py-3',
                      'text-gray-900 dark:text-white',
                      'bg-gray-50 dark:bg-gray-800/50',
                      'shadow-sm ring-1 ring-inset',
                      'ring-gray-200 dark:ring-gray-700/50',
                      'placeholder:text-gray-500 dark:placeholder:text-gray-400',
                      'focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400',
                      'hover:ring-gray-300 dark:hover:ring-gray-600',
                      'transition-shadow duration-200',
                      error && 'ring-red-300 dark:ring-red-500/30 focus:ring-red-500',
                      isSuccess && 'ring-green-300 dark:ring-green-500/30',
                      content.input?.className
                    )}
                    aria-invalid={!!error}
                    disabled={isSubmitting || isSuccess}
                  />
                  {error && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-2" 
                      role="alert"
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                        <span className="h-2 w-2 rounded-full bg-red-600 dark:bg-red-400" />
                      </span>
                      {error}
                    </motion.p>
                  )}
                </div>

                <button
                  type="submit"
                  onClick={async (e) => {
                    e.preventDefault()
                    await handleSubmit(e)
                  }}
                  disabled={isSubmitting || isSuccess}
                  aria-label={isSubmitting ? content.button?.loadingText : content.button?.text}
                  className={cn(
                    'flex w-full items-center justify-center gap-2 rounded-lg',
                    'bg-indigo-600 dark:bg-indigo-500',
                    'px-4 py-3 text-base font-medium text-white shadow-sm',
                    'hover:bg-indigo-500 dark:hover:bg-indigo-400',
                    'focus:outline-none focus:ring-2',
                    'focus:ring-indigo-500 dark:focus:ring-indigo-400',
                    'focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                    'disabled:opacity-50 dark:disabled:opacity-40',
                    'transition-all duration-200',
                    'transform hover:scale-[1.02] active:scale-[0.98]',
                    isSuccess && 'bg-green-600 hover:bg-green-500',
                    content.button?.className
                  )}
                >
                  {isSuccess ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <svg 
                        className="h-5 w-5 text-white" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <motion.path
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.5 }}
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{content.messages?.success}</span>
                    </motion.div>
                  ) : (
                    <span>{isSubmitting ? content.button?.loadingText : content.button?.text}</span>
                  )}
                  {!isSuccess && content.button?.icon}
                </button>
              </div>
            )}

            {content.privacy?.enabled !== false && (
              <div className="mt-6">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {content.privacy?.text}{' '}
                  {content.privacy?.privacyLink && (
                    <a 
                      href={content.privacy.privacyLink.url}
                      className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                      {content.privacy.privacyLink.text}
                    </a>
                  )}
                  {content.privacy?.termsLink?.url && (
                    <>
                      {' and '}
                      <a 
                        href={content.privacy.termsLink?.url ?? `${siteConfig.baseLinks.terms}`}
                        className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        {content.privacy.termsLink?.text ?? 'Terms of Service'}
                      </a>
                    </>
                  )}
                  .
                </p>
                {/* Only render disclaimer if explicitly provided */}
                {content.privacy?.disclaimer && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center">
                    {content.privacy.disclaimer}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Features (if modern preset, show below form) */}
          {preset === 'modern' && content.features && content.features.length > 0 && (
            <div className="grid gap-4 mt-8">
              {content.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  {feature.icon && (
                    <div className="flex-shrink-0 w-5 h-5 text-blue-600 dark:text-blue-400">
                      {feature.icon}
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">{feature.text}</h3>
                    {feature.description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{feature.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }, [theme, content, image, preset])

  return (
    <BaseEmailCaptureForm
      ref={ref}
      theme={theme}
      content={content}
      {...props}
    >
      {renderForm}
    </BaseEmailCaptureForm>
  )
})

SplitEmailCapture.displayName = 'SplitEmailCapture' 